/**
 * AppICEMAP — ReportCard Component
 * Individual report row in the bottom sheet list.
 *
 * Tag: <report-card>
 *
 * Properties:
 *   .report — the report data object
 *   .isSelected — whether this card is currently selected/active
 *   .userLocation — [lat, lng] for distance calculation
 *
 * Dispatches:
 *   report-card:click          → { reportId: string }
 *   report-card:ver-detalle    → { reportId: string }
 *   report-card:quick-confirm  → { reportId: string }
 *   report-card:quick-deny     → { reportId: string }
 *
 * States:
 *   .report-card--active      — accent left border
 *   .report-card--expiring    — 50% opacity + "Expirado" badge
 *   .report-card--low-karma   — low trust badge
 */

import { dispatch } from "../../store/index.js";
import {
	getCategoryStyle,
	categoryIconHtml,
} from "../../utils/categoryStyle.js";
import {
	canDelete,
	confirmAndDeleteReport,
} from "../../services/reportActions.js";
import { getRemainingFraction, formatTimeLeft } from "../../utils/expiry.js";
import { formatDistance } from "../../utils/units.js";

export class ReportCard extends HTMLElement {
	constructor() {
		super();
		this._report = null;
		this._isSelected = false;
		this._userLocation = null;

		// Swipe state
		this._swipeStartX = 0;
		this._swipeOffset = 0;
		this._isSwiping = false;
	}

	connectedCallback() {
		this._render();
		this._bindEvents();
	}

	/* ── Public API ────────────────────────────────────────────────────────── */

	get report() {
		return this._report;
	}
	set report(val) {
		this._report = val;
		this._renderContent();
	}

	get isSelected() {
		return this._isSelected;
	}
	set isSelected(val) {
		this._isSelected = !!val;
		if (this._cardEl) {
			this._cardEl.classList.toggle("report-card--active", this._isSelected);
		}
	}

	get userLocation() {
		return this._userLocation;
	}
	set userLocation(val) {
		this._userLocation = val;
		this._updateDistance();
	}

	/* ── Render ────────────────────────────────────────────────────────────── */

	_render() {
		this.innerHTML = `
      <article class="report-card" role="listitem">
        <div class="report-card__icon-square">
          <i class="ph" aria-hidden="true"></i>
        </div>
        <div class="report-card__body">
          <h5 class="report-card__title"></h5>
          <div class="report-card__meta"></div>
          <div class="report-card__confirmations"></div>
        </div>
        <button class="delete-report-btn delete-report-btn--icon report-card__delete"
                aria-label="Borrar reporte" title="Borrar reporte" hidden>
          <i class="ph ph-trash" aria-hidden="true"></i>
        </button>
        <button class="report-card__action" aria-label="Ver detalle">
          <i class="ph ph-arrow-right" aria-hidden="true"></i>
        </button>
        <div class="report-card__ttl-ring" hidden></div>
      </article>
    `;

		this._cardEl = this.querySelector(".report-card");
		this._iconSquare = this.querySelector(".report-card__icon-square");
		this._icon = this.querySelector(".report-card__icon-square i");
		this._titleEl = this.querySelector(".report-card__title");
		this._metaEl = this.querySelector(".report-card__meta");
		this._confirmEl = this.querySelector(".report-card__confirmations");
		this._actionBtn = this.querySelector(".report-card__action");
		this._deleteBtn = this.querySelector(".report-card__delete");
		this._ttlRing = this.querySelector(".report-card__ttl-ring");

		this._renderContent();
	}

	_renderContent() {
		if (!this._report || !this._cardEl) return;

		const r = this._report;
		const cat = r.category || {};
		const { color, ink, iconSvg, phosphor } = getCategoryStyle(cat);

		// Icon
		if (this._iconSquare) {
			this._iconSquare.style.background = color;
			this._iconSquare.style.color = ink;
		}
		if (this._icon) {
			if (iconSvg) {
				// Swap the <i> placeholder for the bespoke glyph
				this._icon.outerHTML = iconSvg;
				this._icon = this._iconSquare?.querySelector("svg") || null;
			} else {
				// setAttribute, not .className: on a re-render the node may be the
				// <svg> from a previous palette category, whose className is a
				// read-only SVGAnimatedString.
				this._icon.outerHTML = `<i class="ph ph-${phosphor}" aria-hidden="true"></i>`;
				this._icon = this._iconSquare?.querySelector("i") || null;
			}
		}

		// Delete action — admins only
		if (this._deleteBtn) {
			this._deleteBtn.hidden = !canDelete(r);
		}

		// Title
		if (this._titleEl) {
			this._titleEl.textContent = r.title || "Sin título";
		}

		// Meta
		if (this._metaEl) {
			const timeAgo = relativeTime(r.created_at);
			const distance = calcDistance(
				this._userLocation,
				r.location?.coordinates,
			);
			const distanceStr = distance ? formatDistance(distance) : "";

			this._metaEl.innerHTML = `
        <span class="badge badge--category" style="--badge-color:${color}">${escapeHtml(cat.label || "Reporte")}</span>
        <span class="report-card__time">${timeAgo}</span>
        ${distanceStr ? `<span class="report-card__distance">· ${distanceStr}</span>` : ""}
      `;
		}

		// Confirmations
		if (this._confirmEl) {
			const confs = r.confirmation_count || 0;
			const denials = r.denial_count || 0;
			this._confirmEl.innerHTML = `
        <span>✓ ${confs}</span>
        <span>✗ ${denials}</span>
      `;
		}

		// States
		this._cardEl.classList.toggle(
			"report-card--expiring",
			r.status === "expiring",
		);
		this._cardEl.classList.toggle("report-card--low-karma", isLowKarma(r));

		// TTL ring
		this._updateTtlRing(r);

		// Store report id
		this._cardEl.dataset.reportId = r.id;
	}

	_updateTtlRing(report) {
		if (!this._ttlRing) return;

		const fraction = getRemainingFraction(report);

		// Hide the ring when there is nothing meaningful to show
		if (fraction >= 1 || fraction <= 0) {
			this._ttlRing.hidden = fraction >= 1;
			if (fraction >= 1) return;
		}

		const circumference = 2 * Math.PI * 10; // r=10
		const dashOffset = circumference * (1 - fraction);

		let colorClass = "";
		if (fraction < 0.25) colorClass = "ttl-fill--danger";
		else if (fraction < 0.5) colorClass = "ttl-fill--warning";

		this._ttlRing.hidden = false;
		this._ttlRing.title = formatTimeLeft(report);
		this._ttlRing.innerHTML = `
      <svg viewBox="0 0 24 24">
        <circle class="ttl-bg" cx="12" cy="12" r="10"></circle>
        <circle class="ttl-fill ${colorClass}" cx="12" cy="12" r="10"
                stroke-dasharray="${circumference.toFixed(2)}"
                stroke-dashoffset="${dashOffset.toFixed(2)}"></circle>
      </svg>
    `;
	}

	_updateDistance() {
		if (!this._report || !this._metaEl) return;
		const distance = calcDistance(
			this._userLocation,
			this._report.location?.coordinates,
		);
		const distanceStr = distance ? formatDistance(distance) : "";

		// Update or add distance span
		let distEl = this._metaEl.querySelector(".report-card__distance");
		if (distanceStr) {
			if (!distEl) {
				distEl = document.createElement("span");
				distEl.className = "report-card__distance";
				this._metaEl.appendChild(distEl);
			}
			distEl.textContent = `· ${distanceStr}`;
		} else if (distEl) {
			distEl.remove();
		}
	}

	/* ── Events ────────────────────────────────────────────────────────────── */

	_bindEvents() {
		if (!this._cardEl) return;

		// Delete (admins) — stop the click from also selecting the card
		this._deleteBtn?.addEventListener("click", async (e) => {
			e.stopPropagation();
			if (this._report) await confirmAndDeleteReport(this._report);
		});

		// Click → select + show on map
		this._cardEl.addEventListener("click", (e) => {
			if (e.target.closest(".report-card__action")) return; // handled separately
			if (e.target.closest(".report-card__delete")) return; // handled above
			const id = this._report?.id;
			if (id) {
				this.dispatchEvent(
					new CustomEvent("report-card:click", {
						detail: { reportId: id },
						bubbles: true,
					}),
				);
			}
		});

		// Action button → ver detalle
		if (this._actionBtn) {
			this._actionBtn.addEventListener("click", (e) => {
				e.stopPropagation();
				const id = this._report?.id;
				if (id) {
					this.dispatchEvent(
						new CustomEvent("report-card:ver-detalle", {
							detail: { reportId: id },
							bubbles: true,
						}),
					);
				}
			});
		}

		// Touch swipe
		this._cardEl.addEventListener("touchstart", this._onTouchStart.bind(this), {
			passive: true,
		});
		this._cardEl.addEventListener("touchmove", this._onTouchMove.bind(this), {
			passive: true,
		});
		this._cardEl.addEventListener("touchend", this._onTouchEnd.bind(this));
	}

	_onTouchStart(e) {
		this._swipeStartX = e.touches[0].clientX;
		this._swipeOffset = 0;
		this._isSwiping = true;
	}

	_onTouchMove(e) {
		if (!this._isSwiping) return;
		this._swipeOffset = e.touches[0].clientX - this._swipeStartX;
	}

	_onTouchEnd() {
		if (!this._isSwiping) return;
		this._isSwiping = false;

		const threshold = 80;
		const id = this._report?.id;
		if (!id) return;

		if (this._swipeOffset < -threshold) {
			// Swipe left → quick confirm
			this.dispatchEvent(
				new CustomEvent("report-card:quick-confirm", {
					detail: { reportId: id },
					bubbles: true,
				}),
			);
		} else if (this._swipeOffset > threshold) {
			// Swipe right → quick deny
			this.dispatchEvent(
				new CustomEvent("report-card:quick-deny", {
					detail: { reportId: id },
					bubbles: true,
				}),
			);
		}

		// Reset
		if (this._cardEl) {
			this._cardEl.style.transform = "";
			this._cardEl.style.transition = "transform 200ms ease-out";
			setTimeout(() => {
				if (this._cardEl) this._cardEl.style.transition = "";
			}, 200);
		}
	}
}

/* ── Helpers ─────────────────────────────────────────────────────────────── */

function relativeTime(dateStr) {
	if (!dateStr) return "";
	const now = Date.now();
	const then = new Date(dateStr).getTime();
	const diff = now - then;
	const seconds = Math.floor(diff / 1000);

	if (seconds < 60) return "ahora mismo";
	const minutes = Math.floor(seconds / 60);
	if (minutes < 60) return `hace ${minutes} min`;
	const hours = Math.floor(minutes / 60);
	if (hours < 24) return `hace ${hours}h`;
	const days = Math.floor(hours / 24);
	if (days < 7) return `hace ${days}d`;

	return new Date(dateStr).toLocaleDateString("es-MX", {
		month: "short",
		day: "numeric",
	});
}

function calcDistance(userLoc, coords) {
	if (!userLoc || !coords) return null;
	const [lng, lat] = coords;
	const [uLat, uLng] = userLoc;

	const R = 6371; // km
	const dLat = toRad(lat - uLat);
	const dLng = toRad(lng - uLng);
	const a =
		Math.sin(dLat / 2) * Math.sin(dLat / 2) +
		Math.cos(toRad(uLat)) *
			Math.cos(toRad(lat)) *
			Math.sin(dLng / 2) *
			Math.sin(dLng / 2);
	const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
	return R * c;
}

function toRad(deg) {
	return deg * (Math.PI / 180);
}

function isLowKarma(report) {
	const user = report.user_created || {};
	return user.is_limited === true && (report.confirmation_count || 0) < 2;
}

function escapeHtml(str) {
	const div = document.createElement("div");
	div.textContent = str;
	return div.innerHTML;
}

/* ── Attributes ──────────────────────────────────────────────────────────── */

customElements.define("report-card", ReportCard);
